import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GROUP_COURSE_ROLES, formatDietTypeLabel } from './onlineFoodItems';
import { fetchRestaurantMenuMaster, adoptFoodItemsFromMaster } from '../admin/menuMasterApi';
import defaultFoodImage from './assets/DefaultFood.jpg';
import './css/Menu.css';

/**
 * Multi-select picker: adopt global master items into a restaurant MYEATS/DINE-IN menu.
 */
const MasterMenuPicker = ({
  open,
  onClose,
  restaurantId,
  menuSource = 'MYEATS',
  createdById,
  onAdopted,
  categories = [],
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [dietFilter, setDietFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [selected, setSelected] = useState({}); // masterId -> { price, quantity }
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!open || !restaurantId) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchRestaurantMenuMaster({
        restaurantId,
        menuSource,
        foodCategory: categoryFilter || undefined,
        foodType: dietFilter || undefined,
        courseRole: courseFilter || undefined,
        q: search.trim() || undefined,
      });
      setItems(data.items);
    } catch (err) {
      setItems([]);
      setError(err?.response?.data?.error || err?.message || 'Failed to load master menu');
    } finally {
      setLoading(false);
    }
  }, [open, restaurantId, menuSource, categoryFilter, dietFilter, courseFilter, search]);

  useEffect(() => {
    if (open) {
      setSelected({});
      load();
    }
  }, [open, load]);

  const categoryOptions = useMemo(() => {
    if (Array.isArray(categories) && categories.length) {
      return categories
        .map((c) => (typeof c === 'string' ? c : c.food_category_name || c.foodCategoryName || ''))
        .filter(Boolean);
    }
    return [...new Set(items.map((i) => i.foodCategory).filter(Boolean))].sort();
  }, [categories, items]);

  const toggleItem = (item) => {
    if (item.alreadyAdopted) return;
    const id = item.masterFoodItemId;
    setSelected((prev) => {
      const next = { ...prev };
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = { price: '', quantity: '1' };
      }
      return next;
    });
  };

  const selectedCount = Object.keys(selected).length;

  const handleAdopt = async () => {
    const entries = Object.entries(selected);
    if (!entries.length) {
      setError('Select at least one item');
      return;
    }
    for (const [id, draft] of entries) {
      if (!String(draft.price || '').trim()) {
        setError(`Enter a price for each selected item (missing for #${id})`);
        return;
      }
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        menuSource,
        createdById,
        items: entries.map(([id, draft]) => ({
          masterFoodItemId: Number(id),
          price: String(draft.price).trim(),
          quantity: String(draft.quantity || '1').trim() || '1',
        })),
      };
      const result = await adoptFoodItemsFromMaster(restaurantId, payload);
      if (typeof onAdopted === 'function') {
        await onAdopted(result);
      }
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not add from master');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="menu-item-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="menu-item-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="master-menu-picker-title"
        style={{ maxWidth: 720, width: '92vw', maxHeight: '90vh', overflow: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h4 id="master-menu-picker-title">Add from global master</h4>
        <p style={{ marginTop: 0, fontSize: 13, opacity: 0.85 }}>
          Pick catalog items (category · veg/non-veg · course). Set your price. Custom dishes still use Add item.
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
          <input
            type="search"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search master"
          />
          <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} aria-label="Category">
            <option value="">All categories</option>
            {categoryOptions.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          <select value={dietFilter} onChange={(e) => setDietFilter(e.target.value)} aria-label="Diet">
            <option value="">All diets</option>
            <option value="VEG">Veg</option>
            <option value="NON-VEG">Non-Veg</option>
          </select>
          <select value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)} aria-label="Course">
            <option value="">All courses</option>
            {GROUP_COURSE_ROLES.map((role) => (
              <option key={role.value} value={role.value}>{role.label}</option>
            ))}
          </select>
        </div>

        {error ? <p className="menu-template-items-load-error">{error}</p> : null}
        {loading ? <p>Loading…</p> : null}

        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {items.map((item) => {
            const id = item.masterFoodItemId;
            const isOn = Boolean(selected[id]);
            const disabled = item.alreadyAdopted;
            return (
              <li
                key={id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 48px 1fr auto',
                  gap: 10,
                  alignItems: 'center',
                  padding: '8px 0',
                  borderBottom: '1px solid rgba(0,0,0,0.08)',
                  opacity: disabled ? 0.55 : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={isOn || disabled}
                  disabled={disabled || saving}
                  onChange={() => toggleItem(item)}
                  aria-label={`Select ${item.foodItem}`}
                />
                <img
                  src={item.imageUrl || defaultFoodImage}
                  alt=""
                  width={48}
                  height={48}
                  style={{ objectFit: 'cover', borderRadius: 4 }}
                />
                <div>
                  <div style={{ fontWeight: 600 }}>{item.foodItem}</div>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>
                    {item.foodCategory}
                    {' · '}
                    {formatDietTypeLabel(item.foodType)}
                    {item.courseRole ? ` · ${item.courseRole}` : ''}
                    {disabled ? ' · already on menu' : ''}
                  </div>
                  {item.description ? (
                    <div style={{ fontSize: 12, marginTop: 2 }}>{item.description}</div>
                  ) : null}
                </div>
                {isOn ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 100 }}>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="Price"
                      value={selected[id]?.price ?? ''}
                      onChange={(e) =>
                        setSelected((prev) => ({
                          ...prev,
                          [id]: { ...prev[id], price: e.target.value },
                        }))
                      }
                      aria-label={`Price for ${item.foodItem}`}
                    />
                    <input
                      type="text"
                      placeholder="Qty"
                      value={selected[id]?.quantity ?? '1'}
                      onChange={(e) =>
                        setSelected((prev) => ({
                          ...prev,
                          [id]: { ...prev[id], quantity: e.target.value },
                        }))
                      }
                      aria-label={`Quantity for ${item.foodItem}`}
                    />
                  </div>
                ) : (
                  <span />
                )}
              </li>
            );
          })}
        </ul>

        {!loading && items.length === 0 ? (
          <p>No master items match these filters.</p>
        ) : null}

        <div className="menu-item-modal-actions" style={{ marginTop: 16 }}>
          <button type="button" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            type="button"
            className="add-button"
            disabled={saving || selectedCount === 0}
            onClick={handleAdopt}
          >
            {saving ? 'Adding…' : `Add ${selectedCount || ''} to menu`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MasterMenuPicker;
